import { styled } from '@mui/material/styles';
import {
  BackgroundType,
  JobTalent,
  ISkill,
  SkillTalentLevel as SkillLevelType,
  ITalent,
  ItemJobs,
  ItemSkills,
  ItemLanguages,
  IJobTitle,
} from '@bloomays-lib/types.shared';
import { SingleLineText, TalentExperiencesSection, TalentSkillsSection, Modal } from '@bloomays-lib/ui.shared';
import { TitlePart } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import ItemsEdition from './ItemsEdition';
import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  FIND_TALENT,
  LIST_JOBS,
  DELETE_JOBS,
  UPSERT_DIRECT_SKILLS,
  UPSERT_JOB,
  SKILLS,
} from '@bloomays-lib/adapter.api-talent';
import { useTheme } from '@mui/material/styles';
import { notify } from '../../helpers/toastify';
import XPEducationEdition from './XPEducationEdition';
import uniq from 'lodash/uniq';

export type TalentSheetExperienceProps = {
  talent?: ITalent;
};

const TalentSheetExperience = ({ talent }: TalentSheetExperienceProps) => {
  const theme = useTheme();

  const { t } = useTranslation(['talentSheet', 'random']);
  // Les direct skills existant du talent
  const [skills, setSkills] = useState<ItemSkills[]>();
  const [newSkills, setNewSkills] = useState<ItemSkills[]>([]);
  const [experience] = useState<JobTalent>({
    id: '',
    current: false,
    type: BackgroundType.Job,
    name: '',
    jobTitle: undefined,
    talentId: talent?.id,
  });
  // Les skills iso en BDD
  const [listItemsSkill, setListItemsSkill] = useState<string[]>([]);
  const [listItemsJob, setListItemsJob] = useState<string[]>([]);
  const [open, setOpen] = useState<{
    items: ItemJobs[] | ItemSkills[] | ItemLanguages[];
    show: boolean;
    type: 'skills' | 'languages';
  }>({ items: [], show: false, type: 'skills' });
  const [openXP, setOpenXP] = useState<{
    item: JobTalent | undefined;
    show: boolean;
  }>({ item: undefined, show: false });

  const { data: dataSkills } = useQuery<{ listSkills: ISkill[] }>(SKILLS, {
    context: { clientName: 'api.talents' },
    variables: { iso: true },
  });

  const { data: dataJobs } = useQuery<{ listJobTitles: IJobTitle[] }>(LIST_JOBS, {
    variables: {
      iso: true,
    },
    context: { clientName: 'api.talents' },
  });

  const [upsertSkills, { loading: loadingSkills }] = useMutation<{
    upsertDirectSkills: SkillLevelType[];
  }>(UPSERT_DIRECT_SKILLS, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: talent?.id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
  });

  const [upsertJobTalent, { loading: loadingJobTalent }] = useMutation<{
    upsertJobTalent: JobTalent;
  }>(UPSERT_JOB, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: talent?.id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
  });

  const [deleteJobTalent] = useMutation<{
    deleteJobTalent: JobTalent;
  }>(DELETE_JOBS, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: talent?.id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
  });

  const handleClose = () => {
    setOpen({ ...open, show: false });
    setSkills(skills?.filter((skill) => skill.label !== ''));
  };

  const handleCloseXP = () => {
    setOpenXP({ ...openXP, show: false });
  };

  useEffect(() => {
    if (talent) {
      // Les direct skills existant du talent
      setSkills(
        talent.skills?.map((skill) => {
          return {
            id: skill.id as string,
            skillId: skill.skill.id,
            label: skill.skill.label,
            level: skill.level,
            selected: true,
          };
        }),
      );
    }
  }, [talent]);

  useEffect(() => {
    if (dataSkills?.listSkills) {
      // Les skills iso en BDD
      const isoSkills = dataSkills?.listSkills?.map((skill) => skill.label) || [];
      const nonIsoSkills = skills?.map((skill) => skill.label) || [];
      setListItemsSkill(uniq([...isoSkills, ...nonIsoSkills]));
    }
  }, [dataSkills, skills]);

  useEffect(() => {
    if (dataJobs?.listJobTitles) {
      // Les jobs iso en BDD
      setListItemsJob(dataJobs?.listJobTitles?.map((job) => job.label));
    }
  }, [dataJobs]);

  const upsertdirectSkills = async () => {
    const preparedSkills = newSkills.map((skill) => {
      return {
        id: skill.id,
        skill: { label: skill.label, id: skill.skillId },
        level: skill.level,
        talentId: talent?.id,
        isToDelete: skill?.isToDelete,
      };
    });
    const updated = await upsertSkills({
      variables: {
        directSkills: preparedSkills,
      },
    });
    if (updated.data?.upsertDirectSkills && updated.data?.upsertDirectSkills?.length > 0) {
      notify('success', t('modifSaved', { ns: 'random' }), null, {
        hideProgressBar: false,
        autoClose: 3000,
      });
      handleClose();
    }
  };

  const upsertJob = async (xp: JobTalent) => {
    const updated = await upsertJobTalent({
      variables: {
        jobTalent: { ...xp, talentId: talent?.id },
      },
    });
    if (updated.data?.upsertJobTalent && updated.data?.upsertJobTalent?.id) {
      notify('success', t('modifSaved', { ns: 'random' }), null, {
        hideProgressBar: false,
        autoClose: 3000,
      });
      handleCloseXP();
    }
  };

  const deleteJob = async (id?: string) => {
    const deleted = await deleteJobTalent({
      variables: {
        jobTalentId: id,
      },
    });
    if (deleted.data?.deleteJobTalent && deleted.data?.deleteJobTalent?.id) {
      notify('success', t('modifSaved', { ns: 'random' }), null, {
        hideProgressBar: false,
        autoClose: 3000,
      });
      handleCloseXP();
    }
  };

  return (
    <div>
      <Modal
        open={open.show}
        onClose={handleClose}
        additionnalCSS={{
          border: 'none',
          borderRadius: '20px',
          backgroundColor: theme.palette.neutral[10],
          overflowY: 'auto',
        }}
      >
        <ItemsEdition
          talentId={talent?.id}
          loading={loadingSkills}
          items={skills || []}
          type={open.type}
          setNewRow={setSkills}
          setNews={setNewSkills}
          upsert={upsertdirectSkills}
          itemList={listItemsSkill}
        />
      </Modal>
      <Modal
        open={openXP.show}
        onClose={handleCloseXP}
        additionnalCSS={{
          border: 'none',
          borderRadius: '20px',
          backgroundColor: theme.palette.neutral[10],
          overflowY: 'auto',
        }}
      >
        <XPEducationEdition
          loading={loadingJobTalent}
          item={openXP.item}
          type={'XP'}
          upsert={upsertJob}
          deleteXP={deleteJob}
          itemList={listItemsJob}
          itemListSkill={listItemsSkill}
        />
      </Modal>
      <StyledXP>
        <StyledContainerSkillDetails>
          <TitlePart variant="heading6" textTitle={t('description', { ns: 'talentSheet' })} />
          {talent?.summary ? (
            <SingleLineText text={talent.summary} />
          ) : (
            <StyledEmptyState>
              <SingleLineText text={t('noDescription')} />
            </StyledEmptyState>
          )}
        </StyledContainerSkillDetails>
      </StyledXP>
      <TalentSkillsSection
        skills={talent?.skillLevels || []}
        addActionFn={() => {
          setSkills(
            talent?.skills?.map((skill) => {
              return {
                id: skill.id as string,
                skillId: skill.skill.id,
                label: skill.skill.label,
                level: skill.level,
                selected: true,
              };
            }),
          );
          setOpen({
            type: 'skills',
            items: skills || [],
            show: true,
          });
        }}
      />
      <TalentExperiencesSection
        addActionFn={() => {
          setOpenXP({
            item: experience,
            show: true,
          });
        }}
        setOpenXP={setOpenXP}
        jobs={talent?.jobs || []}
      />
    </div>
  );
};

export default TalentSheetExperience;
export { TalentSheetExperience };

const StyledEmptyState = styled('div')(
  () => `
  display: flex;
  justify-content: center;
  padding-top: 24px;
  `,
);

const StyledXP = styled('div')(
  () => `
  box-shadow: 0px 4.30769px 23.6923px rgba(75, 75, 75, 0.09);
  border-radius: 10px;
  text-align: left;
  padding: 24px 24px 32px 24px;
  margin-bottom: 24px;
  `,
);

const StyledContainerSkillDetails = styled('div')(
  () => `
  display: flex;
  flex-flow: column nowrap; 
  gap: 12px;
  `,
);
